import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { useSupabaseCallback } from "../../server/supabase/hooks";
import { SupabaseTagsService } from "./server/supabase-tags-service";

import type { Tag } from "./types";
import type { FC } from "react";

export const TagEditDialog: FC<{
  open: boolean;
  handleClose: () => void;
  tag: Tag;
  reload: () => void;
}> = ({ open, handleClose, tag, reload }) => {
  const [saving, setSaving] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  const [tagName, setTagName] = useState<string>(tag.name);

  const handleSave = useSupabaseCallback(
    async ({ supabase }) => {
      if (saving) return;
      setSaving(true);

      const { error } = await new SupabaseTagsService(supabase).update(tag.id, {
        name: tagName.trim(),
      });

      if (error) {
        if (error?.message.toLowerCase().includes("duplicate key")) {
          toast.error("Tag name must be unique");
        } else {
          toast.error("Saving tag name failed, please try again");
        }
        setSaving(false);
        return;
      }

      toast.success("Tag name updated");
      reload();
      setSaving(false);
      handleClose();
    },
    [handleClose, reload, saving, tag, tagName],
  );

  const onSaveClick = (): void => {
    void handleSave();
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>
        <Box>Edit Tag</Box>
      </DialogTitle>
      <DialogContent>
        <TextField
          inputRef={ref}
          id="tag-name-edit"
          name="tag-name"
          label="Tag name"
          title="Tag name"
          fullWidth
          onChange={(e) => setTagName(e.target.value)}
          value={tagName}
          margin="dense"
        ></TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} data-analytics-id="edit-tag-cancel" variant="text">
          Cancel
        </Button>
        <Button
          onClick={onSaveClick}
          data-analytics-id="edit-tag-update"
          variant="text"
          loading={saving}
          disabled={saving || tagName === tag.name || tagName.trim().length === 0}
        >
          Update Tag Name
        </Button>
      </DialogActions>
    </Dialog>
  );
};
